import React from "react";
import { useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { zodResolver } from "@hookform/resolvers/zod";
import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import { Flex, Text } from "styles/utility-components";
import { z } from "zod";

import { TextArea } from "components/form_components/Inputs";
import Button from "components/ui/button/Button";
import Modal from "components/ui/modal/Modal";
import { assertCurrentUser } from "lib/currentUser";
import { OpportunityQuery } from "lib/generated/app-service-gql/graphql";
import { useCreateComment } from "lib/hooks/api/comments/useCreateComment";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { CommentObjectType } from "lib/types/graphQLEnums";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  opportunityId: string;
  stageName: string;
  onConfirm: () => void;
};

const commentSchema = z.object({
  comment: z.string().optional(),
});

type CommentForm = z.infer<typeof commentSchema>;

export default function NoBidModal({
  isOpen,
  onClose,
  opportunityId,
  stageName,
  onConfirm,
}: Props) {
  const { control, handleSubmit, reset } = useForm<CommentForm>({
    resolver: zodResolver(commentSchema),
    defaultValues: {},
  });

  const queryClient = useQueryClient();
  const user = assertCurrentUser();

  const { mutate: createComment } = useCreateComment({
    onSuccess: async (data) => {
      queryClient.setQueryData(
        ["opportunity", opportunityId],
        (prevData: OpportunityQuery | undefined) => {
          if (!prevData) return prevData;
          return {
            opportunity: {
              ...prevData.opportunity,
              comments: [
                {
                  id: data.createComment.id,
                  content: data.createComment.content,
                  author: {
                    guid: user.guid,
                    firstName: user.first_name,
                    lastName: user.last_name,
                  },
                  createdAt: data.createComment.createdAt,
                  updatedAt: data.createComment.updatedAt,
                },
                ...prevData.opportunity.comments,
              ],
            },
          };
        },
      );
    },
    onError: (e) => {
      Sentry.captureException(e);
    },
  });

  const onSubmit = (values: CommentForm) => {
    if (!opportunityId) return;
    if (values.comment) {
      createComment({
        request: {
          content: values.comment,
          objectId: opportunityId,
          objectType: CommentObjectType.Opportunity,
        },
      });
    }
    onConfirm();
  };

  const handleModalClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} modalClose={handleModalClose} maxWidth="560px" showCloseButton={false}>
      <Content onSubmit={handleSubmit(onSubmit)}>
        <Flex gap={8} alignItems="center">
          <UIcon icon="xCircle" size={20} />
          <Text h3>Marked as {stageName}</Text>
        </Flex>
        <DescriptionContainer>
          <Text>
            Keep your team in the loop by adding a brief note about why you've marked this as{" "}
            {stageName}.
          </Text>
        </DescriptionContainer>
        <TextAreaContainer>
          <TextArea
            rows={4}
            name="comment"
            label=""
            placeholder="Add optional comment"
            control={control}
            aria-label="Comment"
            onClick={(e) => {
              e.stopPropagation();
            }}
            onPressEnter={(e) => {
              if (e.shiftKey) return;

              e.preventDefault();
              e.stopPropagation();
              return handleSubmit(onSubmit)();
            }}
          />
        </TextAreaContainer>
        <ButtonContainer>
          <Button type="reset" buttonType="secondary" onClick={handleModalClose}>
            Cancel
          </Button>
          <Button type="submit">Confirm {stageName}</Button>
        </ButtonContainer>
      </Content>
    </Modal>
  );
}

const Content = styled.form({
  padding: 16,
});

const DescriptionContainer = styled.div({
  marginTop: 8,
});

const TextAreaContainer = styled.div({
  marginTop: 16,
});

const ButtonContainer = styled(Flex)({
  marginTop: 24,
  justifyContent: "flex-end",
  gap: 8,
});
